import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Profile } from '@tekman/onmat/models';
import { StorageService } from '@tekman/shared/service';
import { SubSink } from '@tekman/shared/utils';
import { ApiService } from '@tekman/onmat/service/generic';
import { filter, map, take } from 'rxjs/operators';
import { appRoutes, Language } from '@tekman/onmat/constants';
import { TranslateService } from '@ngx-translate/core';

// VAL keeps its own translations, but the license is displayed as CAT.
const LABEL_MAP: Partial<Record<Language, Language>> = {
  [Language.VAL]: Language.CAT,
};

interface LanguageOption {
  value: string;
  label: string;
}

@Component({
  selector: 'onmat-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrls: ['./language-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  @Input() active: boolean;
  @Input() disabled = false;
  profile: Profile;
  private subscriptions: SubSink = new SubSink();
  readonly languageOptions: Signal<LanguageOption[]>;
  selectedLanguage: string;
  private languageMap: Partial<Record<Language, Language>> = {
    [Language.ECU]: Language.ESP,
    [Language.EUSK]: Language.ESP,
  };

  private resolveLanguage(language: string): string {
    return this.languageMap[language as Language] ?? language;
  }

  constructor(
    private readonly localStorage: StorageService,
    private readonly apiService: ApiService,
    private translateService: TranslateService
  ) {
    this.languageOptions = toSignal(
      this.apiService
        .getLanguages()
        .pipe(map(languages => languages.map(language => ({ value: language, label: LABEL_MAP[language as Language] ?? language })))),
      { initialValue: [] }
    );
  }

  ngOnInit(): void {
    this.apiService
      .getLanguages()
      .pipe(
        filter(languages => !!languages && languages.length > 0),
        take(1)
      )
      .subscribe(languages => {
        this.selectedLanguage = this.resolveLanguage(this.localStorage.languageLicense ?? languages[0]);
        this.translateService.use(this.selectedLanguage);
      });
  }

  manageUrlRedirect() {
    if (location.href.includes(appRoutes.groupEdit)) {
      location.href = appRoutes.groups;
    } else if (location.href.includes(appRoutes.steps) || location.href.includes(appRoutes.activities)) {
      location.href = appRoutes.itineraries;
    } else {
      location.reload();
    }
  }

  onLangChanged(language) {
    this.localStorage.languageLicense = language;
    localStorage.removeItem('selectedGroup');
    this.localStorage.selectedGroupId = undefined;
    this.manageUrlRedirect();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
